import { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "@/components/base/Loading";
import { formatFullDate } from "@/lib/format";
import { downloadCsv } from "@/lib/csv";
import type {
  ChangeRange,
  ChangeType,
  MemberChange,
  ModuleChange,
} from "@/pages/admin/staff/hooks/useStaff";

interface MemberChangeLogProps {
  loadChanges: (type: ChangeType, range?: ChangeRange) => Promise<MemberChange[]>;
  notify: (text: string, tone?: "success" | "error") => void;
}

const FILTERS: { key: ChangeType; label: string }[] = [
  { key: "all", label: "全部变更" },
  { key: "role", label: "角色变更" },
  { key: "permission", label: "权限变更" },
];

const levelTone: Record<string, { chip: string; icon: string }> = {
  success: { chip: "bg-primary-100 text-primary-700", icon: "ri-checkbox-circle-line" },
  info: { chip: "bg-secondary-100 text-secondary-900", icon: "ri-information-line" },
  warning: { chip: "bg-accent-100 text-accent-700", icon: "ri-alert-line" },
  error: { chip: "bg-accent-100 text-accent-700", icon: "ri-error-warning-line" },
};

// 把后端写入的权限状态值统一成可读标签与配色。
function stateMeta(value: string | boolean): { label: string; chip: string } {
  if (value === true || value === "allow") {
    return { label: value === "allow" ? "单独放行" : "允许", chip: "bg-primary-100 text-primary-700" };
  }
  if (value === false || value === "deny") {
    return { label: value === "deny" ? "单独收回" : "禁止", chip: "bg-accent-100 text-accent-700" };
  }
  return { label: "跟随角色", chip: "bg-background-200 text-foreground-600" };
}

function ModuleDiffList({ changes }: { changes: ModuleChange[] }) {
  return (
    <ul className="mt-2 space-y-1.5 rounded-md border border-background-200 bg-background-50 p-3">
      {changes.map((change) => {
        const from = stateMeta(change.from);
        const to = stateMeta(change.to);
        return (
          <li key={change.module} className="flex flex-wrap items-center gap-2 text-xs">
            <span className="w-28 shrink-0 font-medium text-foreground-800">{change.label}</span>
            <span className={`rounded-full px-2 py-0.5 ${from.chip}`}>{from.label}</span>
            <span className="flex h-3 w-3 items-center justify-center text-foreground-400">
              <i className="ri-arrow-right-line" />
            </span>
            <span className={`rounded-full px-2 py-0.5 ${to.chip}`}>{to.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

export default function MemberChangeLog({ loadChanges, notify }: MemberChangeLogProps) {
  const [filter, setFilter] = useState<ChangeType>("all");
  const [changes, setChanges] = useState<MemberChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [keyword, setKeyword] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const load = useCallback(
    async (type: ChangeType, range: ChangeRange) => {
      setLoading(true);
      setError(null);
      try {
        const rows = await loadChanges(type, range);
        setChanges(rows);
      } catch (err) {
        setError(err instanceof Error ? err.message : "变更记录加载失败，请稍后重试");
      } finally {
        setLoading(false);
      }
    },
    [loadChanges],
  );

  useEffect(() => {
    void load(filter, { start, end });
  }, [load, filter, start, end]);

  const filtered = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    if (!term) return changes;
    return changes.filter(
      (item) =>
        (item.actor_name ?? "").toLowerCase().includes(term) ||
        (item.detail ?? "").toLowerCase().includes(term) ||
        item.action.toLowerCase().includes(term),
    );
  }, [changes, keyword]);

  const handleRefresh = () => {
    void load(filter, { start, end });
    notify("已刷新成员变更记录", "success");
  };

  const handleExport = () => {
    if (filtered.length === 0) {
      notify("当前没有可导出的变更记录", "error");
      return;
    }
    const scope = start || end ? `${start || "起"}_${end || "今"}` : new Date().toISOString().slice(0, 10);
    downloadCsv(
      `成员变更台账_${scope}.csv`,
      [
        { key: "time", label: "时间", value: (row) => formatFullDate(row.created_at) },
        { key: "actor", label: "操作人", value: (row) => row.actor_name || "系统" },
        { key: "action", label: "动作", value: (row) => row.action },
        { key: "detail", label: "详情", value: (row) => row.detail || "" },
        { key: "level", label: "级别", value: (row) => row.level || "info" },
      ],
      filtered,
    );
    notify(`已导出 ${filtered.length} 条变更记录`, "success");
  };

  const hasRange = Boolean(start || end);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="font-heading text-base font-semibold text-foreground-950">成员变更记录</h2>
          <p className="mt-1 text-xs text-foreground-500">
            谁调整了成员角色、谁改动了权限矩阵或个人覆盖，全部留痕；权限类变更可直接展开查看具体模块的前后对比，方便审计追责。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-60">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line" />
            </span>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索操作人、对象或动作"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={loading || filtered.length === 0}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-download-2-line" />
            </span>
            导出变更台账
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100"
          >
            <span className="flex h-4 w-4 items-center justify-center text-primary-700">
              <i className="ri-refresh-line" />
            </span>
            刷新
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex rounded-full border border-background-300 bg-background-100 p-1">
          {FILTERS.map((item) => {
            const active = item.key === filter;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setFilter(item.key)}
                className={`inline-flex cursor-pointer items-center whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                  active ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-foreground-600">
            起始
            <input
              type="date"
              value={start}
              onChange={(event) => setStart(event.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </label>
          <span className="text-xs text-foreground-400">—</span>
          <label className="flex items-center gap-2 text-xs text-foreground-600">
            截止
            <input
              type="date"
              value={end}
              onChange={(event) => setEnd(event.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </label>
          {hasRange && (
            <button
              type="button"
              onClick={() => {
                setStart("");
                setEnd("");
              }}
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:bg-background-100"
            >
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-close-line" />
              </span>
              清除
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="mt-5 space-y-3">
          {[0, 1, 2].map((item) => (
            <Skeleton key={item} className="h-14 w-full" />
          ))}
        </div>
      ) : error ? (
        <div className="mt-5 flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-12 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-error-warning-line text-xl" />
          </span>
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={() => void load(filter, { start, end })}
            className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            重新加载
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 py-12 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-file-history-line text-xl" />
          </span>
          <p className="text-sm text-foreground-600">
            {hasRange ? "所选时间范围内暂无成员变更记录" : "暂无成员变更记录"}
          </p>
        </div>
      ) : (
        <>
          <p className="mt-4 text-xs text-foreground-500">
            命中 {filtered.length} 条记录{hasRange && `（${start || "起"} 至 ${end || "今"}）`}
          </p>
          <ul className="mt-3 space-y-2">
            {filtered.map((item) => {
              const tone = levelTone[item.level ?? "info"] ?? levelTone.info;
              const changes = item.meta?.changes ?? [];
              const hasDiff = changes.length > 0;
              const isOpen = expanded[item.id] === true;
              return (
                <li key={item.id} className="rounded-md border border-background-200 bg-background-100 px-4 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${tone.chip}`}>
                        <i className={`${tone.icon} text-base`} />
                      </span>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium text-foreground-950">{item.action}</p>
                          {hasDiff && (
                            <button
                              type="button"
                              onClick={() => setExpanded((prev) => ({ ...prev, [item.id]: !isOpen }))}
                              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                            >
                              <span className="flex h-3 w-3 items-center justify-center">
                                <i className={isOpen ? "ri-subtract-line" : "ri-list-check-2"} />
                              </span>
                              {isOpen ? "收起明细" : `查看前后对比（${changes.length}）`}
                            </button>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-foreground-600">{item.detail || "—"}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-foreground-700">操作人：{item.actor_name || "系统"}</p>
                      <p className="mt-0.5 text-xs text-foreground-500">{formatFullDate(item.created_at)}</p>
                    </div>
                  </div>
                  {hasDiff && isOpen && <ModuleDiffList changes={changes} />}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}